import React, { useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { parseError } from '../../utils/ethers';
import { Spinner } from '../common/Loader';

// Convert user-entered percentage string ("10.5") to bps integer (1050).
function pctToBps(pctStr) {
  const n = Number(pctStr);
  if (!isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
}

function bpsToPctStr(bps) {
  const n = Number(bps) / 100;
  return String(parseFloat(n.toFixed(2)));
}

export default function WalletsPanel() {
  const { read, write } = useContracts();
  const notify = useNotify();

  const [team, setTeam] = useState({ wallets: [], pcts: [] });
  const [dev, setDev] = useState({ wallets: [], pcts: [] });
  const [busy, setBusy] = useState('');

  const load = () => {
    Promise.all([
      read.storage.getTeamWallets(),
      read.storage.getDevWallets(),
      read.storage.getTeamWalletBps(),
      read.storage.getDevWalletBps(),
    ]).then(([tw, dw, tb, db]) => {
      setTeam({
        wallets: tw.slice(),
        pcts: tb.map((b) => bpsToPctStr(b)),
      });
      setDev({
        wallets: dw.slice(),
        pcts: db.map((b) => bpsToPctStr(b)),
      });
    }).catch(() => {});
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function saveWallet(group, idx, addr) {
    if (!write) return notify.error('Wallet not connected.');
    if (!ethers.utils.isAddress(addr)) return notify.error('Invalid address.');
    const key = `${group}-w-${idx}`;
    setBusy(key);
    try {
      const tx = group === 'team'
        ? await write.storage.setTeamWallet(idx, addr)
        : await write.storage.setDevWallet(idx, addr);
      notify.info('Updating wallet...');
      await tx.wait();
      notify.success('Wallet updated');
      load();
    } catch (err) {
      notify.error(parseError(err));
    } finally { setBusy(''); }
  }

  async function savePct(group, idx, pct) {
    if (!write) return notify.error('Wallet not connected.');
    const n = Number(pct);
    if (!isFinite(n) || n < 0 || n > 100) return notify.error('Enter a valid percentage (0–100).');
    const bps = pctToBps(pct);
    const key = `${group}-b-${idx}`;
    setBusy(key);
    try {
      const tx = group === 'team'
        ? await write.storage.setTeamWalletBps(idx, bps)
        : await write.storage.setDevWalletBps(idx, bps);
      notify.info('Updating share...');
      await tx.wait();
      notify.success('Share updated');
      load();
    } catch (err) {
      notify.error(parseError(err));
    } finally { setBusy(''); }
  }

  // Total in % (as float)
  const totalPct = [...team.pcts, ...dev.pcts]
    .reduce((a, b) => a + (Number(b) || 0), 0);
  const remainderPct = Math.max(0, 100 - totalPct);
  const pctOk = totalPct <= 100;

  return (
    <div className="admin-panel">
      <h3>Wallets & Distribution</h3>
      <p className="panel-hint">Team + dev wallets and their fund-distribution shares (%). Total must not exceed 100%.</p>

      <div className={`bps-sum ${pctOk ? 'ok' : 'warn'}`}>
        <span>Total Distribution</span>
        <strong>
          {totalPct.toFixed(2)}% · Remainder to contract: {remainderPct.toFixed(2)}%
        </strong>
      </div>

      <h4 style={{ marginTop: 24, marginBottom: 12, color: 'var(--accent)' }}>Team Wallets (4)</h4>
      {team.wallets.map((addr, i) => (
        <WalletRow key={`t-${i}`} idx={i} addr={addr} pct={team.pcts[i]}
          onSaveAddr={(v) => saveWallet('team', i, v)}
          onSavePct={(v) => savePct('team', i, v)}
          busyA={busy === `team-w-${i}`} busyB={busy === `team-b-${i}`}
        />
      ))}

      <h4 style={{ marginTop: 24, marginBottom: 12, color: 'var(--accent)' }}>Dev Wallets (5)</h4>
      {dev.wallets.map((addr, i) => (
        <WalletRow key={`d-${i}`} idx={i} addr={addr} pct={dev.pcts[i]}
          onSaveAddr={(v) => saveWallet('dev', i, v)}
          onSavePct={(v) => savePct('dev', i, v)}
          busyA={busy === `dev-w-${i}`} busyB={busy === `dev-b-${i}`}
        />
      ))}
    </div>
  );
}

function WalletRow({ idx, addr, pct, onSaveAddr, onSavePct, busyA, busyB }) {
  const [a, setA] = useState(addr);
  const [p, setP] = useState(pct);
  useEffect(() => { setA(addr); }, [addr]);
  useEffect(() => { setP(pct); }, [pct]);

  const addrChanged = a !== addr;
  const pctChanged  = p !== pct;
  const busy = busyA || busyB;

  return (
    <div className="wallet-row">
      <div className="idx">#{idx}</div>

      <div className="wallet-fields">
        <div className="wallet-field-group">
          <span className="field-label">Address</span>
          <input
            className="small-input"
            value={a}
            onChange={(e) => setA(e.target.value)}
            placeholder="0x..."
            spellCheck={false}
          />
        </div>
        <div className="wallet-field-group has-suffix">
          <span className="field-label">Share</span>
          <input
            className="small-input"
            value={p}
            onChange={(e) => setP(e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="0.00"
          />
          <span className="suffix">%</span>
        </div>
      </div>

      <div className="wallet-actions">
        <button
          className="btn btn-secondary"
          disabled={busy || !addrChanged}
          onClick={() => onSaveAddr(a)}
        >
          {busyA ? <Spinner /> : 'Save Address'}
        </button>
        <button
          className="btn btn-secondary"
          disabled={busy || !pctChanged}
          onClick={() => onSavePct(p)}
        >
          {busyB ? <Spinner /> : 'Save Share'}
        </button>
      </div>
    </div>
  );
}
